import { FC, ReactNode } from 'react';
import { Box, IconButton, Tooltip } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useDeactivation } from '../hooks/use-deactivation';
import { ReviewNumber } from '../utils/field-key';

interface DeactivatableFieldProps {
  review: ReviewNumber;
  screen: string;
  field: string; // backend/save key
  // Horizontal offset (px) of the deactivate control from the field's top-right
  // corner. Defaults to 4; bump it for the rare field that already renders its
  // own top-right icon (e.g. the NA toggle) so the two don't overlap.
  controlRight?: number;
  children: ReactNode;
}

// Wraps a single editable field/section on a review page. When the field is
// deactivated (user- or company-level) it renders nothing. Otherwise, a project
// admin sees a small always-visible eye-off control to deactivate it. Reactivation
// happens from the Reports "Deactivated" tab.
export const DeactivatableField: FC<DeactivatableFieldProps> = ({
  review,
  screen,
  field,
  controlRight = 4,
  children,
}) => {
  const { t } = useTranslation();
  const { fieldKey, isFieldKeyOff, isFieldKeyMandatory, canManage, deactivateFieldKey } =
    useDeactivation();
  const key = fieldKey(review, screen, field);

  if (isFieldKeyOff(key)) {
    return null;
  }

  const showControl = canManage && !isFieldKeyMandatory(key);

  return (
    <Box sx={{ position: 'relative' }}>
      {showControl && (
        <Tooltip title={t('deactivate_field')} arrow>
          <IconButton
            size="small"
            tabIndex={-1}
            onClick={() => deactivateFieldKey(key)}
            sx={{
              position: 'absolute',
              top: 4,
              right: controlRight,
              zIndex: 2,
              color: 'text.secondary',
            }}
          >
            <FontAwesomeIcon icon={faEyeSlash as IconProp} size="xs" />
          </IconButton>
        </Tooltip>
      )}
      {children}
    </Box>
  );
};

interface DeactivateColumnControlProps {
  fieldKey: string;
}

// Small inline control for table column headers (the table itself decides
// whether to render the column at all, based on useDeactivation()).
export const DeactivateColumnControl: FC<DeactivateColumnControlProps> = ({ fieldKey }) => {
  const { t } = useTranslation();
  const { canManage, isFieldKeyMandatory, deactivateFieldKey } = useDeactivation();

  if (!canManage || isFieldKeyMandatory(fieldKey)) {
    return null;
  }

  return (
    <Tooltip title={t('deactivate_field')} arrow>
      <IconButton
        size="small"
        onClick={() => deactivateFieldKey(fieldKey)}
        sx={{ ml: 0.5, color: 'text.secondary', opacity: 0.5, '&:hover': { opacity: 1 } }}
      >
        <FontAwesomeIcon icon={faEyeSlash as IconProp} size="xs" />
      </IconButton>
    </Tooltip>
  );
};

interface DeactivateTabControlProps {
  tabKey: string;
}

// Inline control for a review tab/page. Lives inside a navigating <Tab>, so the
// click is stopped from triggering navigation before deactivating the page.
export const DeactivateTabControl: FC<DeactivateTabControlProps> = ({ tabKey }) => {
  const { t } = useTranslation();
  const { canManage, isTabKeyMandatory, deactivateTabKey } = useDeactivation();

  if (!canManage || isTabKeyMandatory(tabKey)) {
    return null;
  }

  return (
    <Tooltip title={t('deactivate_page')} arrow>
      <IconButton
        size="small"
        component="span"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          deactivateTabKey(tabKey);
        }}
        sx={{ ml: 0.5, color: 'text.secondary', opacity: 0.5, '&:hover': { opacity: 1 } }}
      >
        <FontAwesomeIcon icon={faEyeSlash as IconProp} size="xs" />
      </IconButton>
    </Tooltip>
  );
};
